import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import Maybe from "components/__new/common/Maybe";
import useLoginInfo from "hooks/useLoginInfo";
import * as approvalAction from "store/entities/approvals/action";
import useInlinePageRouter from "components/__new/general/useInlinePageRouter";
import IconEmptyBox from "assets/images/icons/project-icons/empty-box.png";
import processParams from "utils/processParams";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import { ReactComponent as IconApprovedRequest } from 'assets/images/icons/icon-approved-request.svg'
import { ReactComponent as IconTotalRequest } from 'assets/images/icons/icon-total-request.svg'
import { ReactComponent as IconPendingRequest } from 'assets/images/icons/icon-pending-request.svg'
import { ReactComponent as IconRejectedRequest } from 'assets/images/icons/icon-rejected-requests.svg'
import SearchBox from "components/__new/common/SearchBox";
import ContentLoading from "components/__new/common/ContentLoading";
import pluralize from "utils/pluralize";
import Filter from "components/__new/common/Filter";
import Table from "components/__new/common/Table";
import { TableContent } from "components/__new/common/TableContent";
import DateFormat, { TimeFormat } from "utils/dateFormat";
import { renderStatusColor } from "utils/statusColor";
import Pagination from "components/__new/common/pagination";
import RequestDetailsWrapper from "../../../components/__new/approval/dashboard/RequestDetailsWrapper";
import { getRandomColor } from "utils/randomColor";
import ApproveRequest from "../../../components/__new/approval/dashboard/ApproveRequest";
import RejectRequest from "../../../components/__new/approval/dashboard/RejectRequest";
import { capitalizeFirstLetter } from "utils/stringFormatter";
import CancelRequest from "components/__new/approval/dashboard/CancelRequest";

const Index = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const {userInfo} = useLoginInfo();
    const { InlineRouterTab } = useInlinePageRouter();
    const {urlQueryParams} = useUrlQueryParams();
    const {
        requestAnalyticsLoading,
        requestAnalytics,
        requestsLoading,
        requests,
        requestComments,
    } = useSelector((s) => s.entities.approvals);

    const printComponentRef = useRef();
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });
    const [request, setRequest] = useState({});
    const [isOpenRequestDetail, setIsOpenRequestDetail] = useState(false);
    const [isOpenApproveRequest, setIsOpenApproveRequest] = useState(false)
    const [isOpenRejectRequest, setIsOpenRejectRequest] = useState(false)
    const [isOpenCancelRequest, setIsOpenCancelRequest] = useState(false)

    const viewDetails = (request) => {
      if (request) {
        setRequest(request);
        setIsOpenRequestDetail(true);
        dispatch(approvalAction.getRequestComments(request?.id));
      }
    }

    const getRequests = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "search", "string", "", "search");
        params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
        params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");
        params = processParams(urlQueryParams, params, "request-type", "string", "", "search");
        params = processParams(urlQueryParams, params, "dateRange", "string", "", "dateRange");
        params = processParams(urlQueryParams, params, "status", "string", "", "status");

        setListParams(params);

        dispatch(approvalAction.getRequestAnalytics());
        dispatch(approvalAction.getRequests({...params, employerId: userInfo?.employer?._id}));
    }

    useEffect(() => {
        getRequests();
    }, [location]);

    return (
        <div>

            <DynamicHeadTag
                headerText="Approvals"
            />

            <ApprovalRequestDetails
                isOpen={isOpenRequestDetail}
                setIsOpen={setIsOpenRequestDetail}
                requestComments={requestComments}
                request={request}
                getRequests={getRequests}
                onOpenApproveRequest={() => setIsOpenApproveRequest(true)}
                onOpenRejectRequest={() => setIsOpenRejectRequest(true)}
                onOpenCancelRequest={() => setIsOpenCancelRequest(true)}
            />

            <ApproveRequest
              isOpen={isOpenApproveRequest}
              setIsOpen={setIsOpenApproveRequest}
              request={request}
              onOpenApproveRequest={() => setIsOpenApproveRequest(false)}
              getRequests={getRequests}
            />

            <RejectRequest
              isOpen={isOpenRejectRequest}
              setIsOpen={setIsOpenRejectRequest}
              request={request}
              onOpenRejectRequest={() => setIsOpenRejectRequest(false)}
              getRequests={getRequests}
            />

            <CancelRequest
              isOpen={isOpenCancelRequest}
              setIsOpen={setIsOpenCancelRequest}
              request={request}
              onOpenCancelRequest={() => setIsOpenCancelRequest(false)}
              getRequests={getRequests}
            />

            <div className="w-full lg:w-1/2 mb-5">
                <div className="page-title flex">
                    Approvals
                </div>
            </div>

            <div className="flex items-center">
              <InlineRouterTab
                items={[
                    {
                        title: "Approval Dashboard",
                        path: "/approvals/dashboard"
                    },
                    {
                        title: "Approval Workflow",
                        path: "/approvals/workflow"
                    }
                ]}
              />
            </div>

            {/* Approval Dashboard Cards */}
            <div className="my-5">
              <div className="flex flex-col lg:flex-row xl:flex-row gap-6">
                    <ApprovalDashboardCard>
                        <div><IconTotalRequest /></div>
                        <div>
                            <div className="text-sm">Total Requests</div>
                            <div className="font-semibold text-3xl">
                                <ContentLoading
                                    loading={requestAnalyticsLoading}
                                    data={<>{requestAnalytics?.data?.totalRequest ?? 0}</>}
                                />
                            </div>
                        </div>
                    </ApprovalDashboardCard>

                    <ApprovalDashboardCard>
                        <div><IconApprovedRequest /></div>
                        <div>
                            <div className="text-sm">Approved Requests</div>
                            <div className="font-semibold text-3xl">
                                <ContentLoading
                                    loading={requestAnalyticsLoading}
                                    data={<>{requestAnalytics?.data?.approvedRequest ?? 0}</>}
                                />
                            </div>
                        </div>
                    </ApprovalDashboardCard>

                    <ApprovalDashboardCard>
                        <div><IconPendingRequest /></div>
                        <div>
                            <div className="text-sm">Pending Requests</div>
                            <div className="font-semibold text-3xl">
                                <ContentLoading
                                    loading={requestAnalyticsLoading}
                                    data={<>{requestAnalytics?.data?.pendingRequest ?? 0}</>}
                                />
                            </div>
                        </div>
                    </ApprovalDashboardCard>

                    <ApprovalDashboardCard>
                        <div><IconRejectedRequest /></div>
                        <div>
                            <div className="text-sm">Rejected Requests</div>
                            <div className="font-semibold text-3xl">
                                <ContentLoading
                                    loading={requestAnalyticsLoading}
                                    data={<>{requestAnalytics?.data?.rejectedRequest ?? 0}</>}
                                />
                            </div>
                        </div>
                    </ApprovalDashboardCard>
              </div>
            </div>

            <div className="mt-6 box">
                <div className="flex items-center justify-start w-max space-x-3">
                    <div className="font-medium">Approval Requests</div>
                    <div className="label h-6 text-sm font-normal text-ep-primary-600 bg-[#ecf2f2]">
                        <ContentLoading
                            loading={requestsLoading}
                            data={<>{requests?.data?.totalDocs ?? 0} {pluralize(requests?.data?.totalDocs, "request", "requests")}</>}
                        />
                    </div>
                </div>

                <div className="mt-6 flex flex-wrap justify-between">
                        <>
                            <div className="max-w-sm flex-grow sm:mr-6">
                                <SearchBox placeholder="Search" name="search" extraUrlParams="page=1" />
                            </div>
                            <div className="flex flex-wrap space-x-3 sm:space-x-3">
                                <div>
                                    <Filter filters={[
                                        {
                                            title: "Date range",
                                            name: "dateRange",
                                            dataType: "custom-timeframe",
                                            hasResetButton: true,
                                            options: [
                                                {
                                                    display: "Today",
                                                    payload: "today",
                                                },
                                                {
                                                    display: "Last 7 days",
                                                    payload: "7days",
                                                },
                                                {
                                                    display: "30 days",
                                                    payload: "30days",
                                                },
                                                {
                                                    display: "1 Year",
                                                    payload: "1year",
                                                },
                                            ],
                                        },
                                        {
                                            title: "Custom Date Range",
                                            dataType: "date-range",
                                            options: {
                                                from: {
                                                    name: "startDate",
                                                    placeholder: "Start Date",
                                                },
                                                to: {
                                                    name: "endDate",
                                                    placeholder: "End Date",
                                                },
                                            },
                                        },
                                        {
                                            title: "Request Type",
                                            name: "request-type",
                                            dataType: "select",
                                            options: [
                                                {
                                                    display: "All",
                                                    payload: "",
                                                },
                                                {
                                                    display: "Transfer",
                                                    payload: "transfer",
                                                },
                                                {
                                                    display: "Multiple Transfer",
                                                    payload: "multiple transfer",
                                                },
                                              //   {
                                              //     display: "Bulk Transfer",
                                              //     payload: "bulk transfer"
                                              //   },
                                                {
                                                  display: "Bill Payment",
                                                  payload: "bill payment"
                                                },
                                              //   {
                                              //     display: "Multiple bill payment",
                                              //     payload: "multiple bill payment"
                                              //   }
                                            ],
                                          },
                                        {
                                            title: "Status",
                                            name: "status",
                                            dataType: "select",
                                            options: [
                                                {
                                                    display: "All",
                                                    payload: "",
                                                },
                                                {
                                                    display: "Approved",
                                                    payload: "approved",
                                                },
                                                {
                                                    display: "Pending",
                                                    payload: "pending",
                                                },
                                                {
                                                    display: "Rejected",
                                                    payload: "rejected",
                                                },
                                            ],
                                        },
                                    ]}/>
                                </div>
                            </div>
                        </>
                </div>
            </div>

            <div className="bg-white">
                <div ref={printComponentRef}>
                    <div className="mb-4s text-xl hidden print:block">
                        Approval Requests
                    </div>

                    <Table classTable="table-hover">
                        <Table.Head>
                            <th>Workflow Title</th>
                            <th>Request Type</th>
                            <th>Initiator</th>
                            <th>Date Requested</th>
                            <th>Status</th>
                            {/* <th>Approver</th> */}
                        </Table.Head>
                        <Table.Body>

                          <TableContent
                              loading={requestsLoading}
                              loadingText="Loading approval requests"
                              data={requests?.data?.data}
                              colspan={6}
                              emptyUI={<>
                                  <div className="py-20 mx-auto text-center text-base">
                                      <div className="flex justify-center">
                                          <img src={IconEmptyBox} alt="Empty" className="h-48" />
                                      </div>
                                      <div className="text-[#101828] font-medium">
                                          No Data Available Yet
                                      </div>
                                      <div className="mt-1 text-[#4A5564]">
                                          Once you create a workflow, it will appear here
                                      </div>
                                  </div>
                              </>}
                          />

                          {requestsLoading === false && requests?.data?.data?.map((request, index) => {
                              return (
                                  <tr key={index} onClick={() => viewDetails(request)}>
                                      <td className="capitalize">
                                        {request?.workflows?.title || "-"}
                                      </td>
                                      <td className="capitalize">
                                          {request?.workflows?.type || "-"}
                                      </td>
                                      <td className="!text-[#101828]">
                                          <div className="flex items-center space-x-3">
                                              <div className="rounded-full w-12 h-12 flex justify-center items-center" style={{backgroundColor: getRandomColor()}}>
                                                  <div className="text-lg font-normal text-white">
                                                      {request?.initiator?.firstName?.charAt().toUpperCase()}{request?.initiator?.lastName?.charAt().toUpperCase()}
                                                  </div>
                                              </div>
                                              <div>
                                                  <div className="text-[#101828] font-medium my-1">{request?.initiator?.firstName} {request?.initiator?.lastName}</div>
                                                  <div className="my-1 text-[#667085]">{request?.initiator?.email}</div>
                                              </div>
                                          </div>
                                      </td>
                                      <td style={{ width: '180px' }}>
                                          <div className="py-2.5">
                                              <div className="mb-1">{DateFormat(request?.createdAt)} at {TimeFormat(request?.createdAt)}</div>
                                          </div>
                                      </td>
                                      <td>
                                          <div className={"label capitalize " + renderStatusColor(request?.status)}>
                                              {request?.status}
                                          </div>
                                      </td>
                                      {/* <td>
                                          <div className="flex items-center space-x-3">
                                              <div className="rounded-full w-12 h-12 flex justify-center items-center" style={{backgroundColor: getRandomColor()}}>
                                                  <div className="text-lg font-normal text-white">
                                                      {request?.approver?.firstName?.charAt().toUpperCase()}{request?.approver?.lastName?.charAt().toUpperCase()}
                                                  </div>
                                              </div>
                                              <div>
                                                  <div className="text-[#101828] font-medium my-1">{request?.approver?.firstName} {request?.approver?.lastName}</div>
                                                  <div className="my-1">{request?.approver?.email}</div>
                                              </div>
                                          </div>
                                      </td> */}
                                  </tr>
                                )
                              })}

                        </Table.Body>
                    </Table>

                    <Maybe condition={requestsLoading === false && requests?.data?.data?.length > 0}>
                        <div className="mt-6">
                            <Pagination data={requests?.data} />
                        </div>
                    </Maybe>
                </div>
            </div>

        </div>
    )
}

const ApprovalRequestDetails = ({request, getRequests, requestComments, setIsOpen, isOpen, onOpenApproveRequest, onOpenRejectRequest, onOpenCancelRequest}) => {

  return (
      <RequestDetailsWrapper
          title={capitalizeFirstLetter(request?.workflows?.title)}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          request={request}
          getRequests={getRequests}
          requestComments={requestComments}
          onOpenApproveRequest={onOpenApproveRequest}
          onOpenRejectRequest={onOpenRejectRequest}
          onOpenCancelRequest={onOpenCancelRequest}
      />
  )
}

const ApprovalDashboardCard = ({ children }) => {
    return (
        <div className="flex-1 bg-white p-5 rounded-xl">
            <div className="flex flex-col justify-between gap-6">
                {children}
            </div>
        </div>
    )
};

export default Index;